import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DataTable from "@/components/DataTable";
import { toast } from "sonner";
import {
  DownloadSimpleIcon, ArrowsClockwiseIcon,
  UserPlusIcon, ProhibitIcon, ArrowClockwiseIcon,
  KeyIcon, ShieldCheckIcon, ShieldSlashIcon,
  EnvelopeSimpleIcon, TrashIcon, CheckCircleIcon,
} from "@phosphor-icons/react";

// Visual metadata per action type
const ACTION_META = {
  "user.disable":       { label: "Disabled",         color: "#DC2626", bg: "#FEE2E2", Icon: ProhibitIcon },
  "user.reactivate":    { label: "Re-enabled",       color: "#059669", bg: "#DCFCE7", Icon: ArrowClockwiseIcon },
  "user.role_change":   { label: "Role changed",     color: "#0176D3", bg: "#EFF6FF", Icon: ArrowsClockwiseIcon },
  "invite.create":      { label: "Invited",          color: "#0176D3", bg: "#EFF6FF", Icon: UserPlusIcon },
  "invite.revoke":      { label: "Invite revoked",   color: "#DC2626", bg: "#FEE2E2", Icon: TrashIcon },
  "invite.accepted":    { label: "Invite accepted",  color: "#059669", bg: "#DCFCE7", Icon: CheckCircleIcon },
  "invite.reminder_sent": { label: "Reminder sent",  color: "#D97706", bg: "#FEF3C7", Icon: EnvelopeSimpleIcon },
  "auth.password_change": { label: "Password changed", color: "#475569", bg: "#F1F5F9", Icon: KeyIcon },
  "auth.2fa_enabled":   { label: "2FA enabled",      color: "#059669", bg: "#DCFCE7", Icon: ShieldCheckIcon },
  "auth.2fa_disabled":  { label: "2FA disabled",     color: "#D97706", bg: "#FEF3C7", Icon: ShieldSlashIcon },
};

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

// Column model for the shared DataTable. When/Action/Actor are fixed widths;
// Target and Details are elastic, so hiding one in the picker hands its width
// to the other.
const AUDIT_COLUMNS = [
  {
    key: "when", label: "When", locked: true, cls: "px-4", weight: 168,
    cell: (r) => (
      <div className="min-w-0">
        <div className="font-mono text-sm text-[#0F172A]">{relativeTime(r.created_at)}</div>
        <div className="break-words font-mono text-[10px] text-[#94A3B8]">{r.created_at?.slice(0, 19).replace("T", " ")}</div>
      </div>
    ),
  },
  {
    key: "action", label: "Action", cls: "px-4", weight: 156,
    cell: (r) => {
      const meta = ACTION_META[r.action] || { label: r.action, color: "#475569", bg: "#F1F5F9", Icon: ArrowsClockwiseIcon };
      const Icon = meta.Icon;
      return (
        <span
          className="inline-flex max-w-full items-center gap-1.5 break-words rounded-sm border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider"
          style={{ background: meta.bg, color: meta.color, borderColor: meta.color + "55" }}
          title={meta.label}
        >
          <Icon size={11} weight="fill" /> {meta.label}
        </span>
      );
    },
  },
  {
    key: "actor", label: "Actor", cls: "px-4", weight: 200,
    cell: (r) => (
      <div className="min-w-0">
        <div className="break-words text-sm font-medium text-[#0F172A]" title={r.actor_name || ""}>{r.actor_name || "—"}</div>
        <div className="break-words font-mono text-[10px] text-[#94A3B8]" title={r.actor_email || ""}>{r.actor_email || ""}</div>
      </div>
    ),
  },
  {
    key: "target", label: "Target", cls: "px-4",
    cell: (r) => (
      <div className="min-w-0">
        <div className="break-words text-sm text-[#0F172A]" title={r.target_label || ""}>{r.target_label || "—"}</div>
        {r.target_ids?.length > 1 && (
          <div className="font-mono text-[10px] text-[#94A3B8]">+{r.target_ids.length - 1} more</div>
        )}
      </div>
    ),
  },
  {
    key: "details", label: "Details", cls: "px-4",
    cell: (r) =>
      r.metadata && Object.keys(r.metadata).length > 0 ? (
        <div className="break-words font-mono text-[11px] text-[#475569]" title={JSON.stringify(r.metadata)}>
          {Object.entries(r.metadata).slice(0, 3).map(([k, v]) => (
            <span key={k} className="mr-2"><span className="text-[#94A3B8]">{k}:</span> {typeof v === "string" ? v : JSON.stringify(v)}</span>
          ))}
        </div>
      ) : null,
  },
];

export default function AuditLogPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [actions, setActions] = useState([]);
  const [action, setAction] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [logs, acts] = await Promise.all([
        api.get("/audit-logs", { params: { action, q: q || undefined, limit: 500 } }),
        api.get("/audit-logs/actions"),
      ]);
      setRows(logs.data);
      setActions(acts.data);
    } catch (e) {
      toast.error("Failed to load audit log");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [action]);

  const downloadCsv = async () => {
    try {
      const res = await api.get("/audit-logs/export.csv", {
        params: { action },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-")}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const needle = q.toLowerCase();
    return rows.filter((r) =>
      (r.actor_name || "").toLowerCase().includes(needle) ||
      (r.actor_email || "").toLowerCase().includes(needle) ||
      (r.target_label || "").toLowerCase().includes(needle),
    );
  }, [rows, q]);


  if (user?.role !== "admin") {
    return (
      <AppLayout>
        <div className="px-8 py-10 text-sm text-[#475569]">
          Admins only. <Link to="/" className="text-[#0176D3] hover:underline">Back to dashboard</Link>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="trk-sticky-header sticky top-0 z-20 px-8 py-4 flex items-center justify-between">
        <div>
          <div className="trk-uppercase-label">Compliance</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Audit log</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs font-mono text-zinc-500" data-testid="audit-count">
            {filtered.length} event{filtered.length === 1 ? "" : "s"}
          </div>
          <Button variant="outline" onClick={load} data-testid="audit-refresh-btn" className="h-9 rounded-md">
            <ArrowsClockwiseIcon size={14} className="mr-1.5" /> Refresh
          </Button>
          <Button onClick={downloadCsv} data-testid="audit-export-csv-btn"
                  className="h-9 rounded-md bg-[#0176D3] hover:bg-[#005FB2] text-white">
            <DownloadSimpleIcon size={14} weight="bold" className="mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="px-8 py-4 flex flex-wrap items-center gap-3">
        <div className="min-w-[240px]">
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger data-testid="audit-action-filter" className="h-9 rounded-md"><SelectValue placeholder="All actions" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {/* Sorted by the LABEL the reader sees, not the raw action key —
                  "user_disabled" and its label "Deactivated" sort nowhere near
                  each other. */}
              {[...actions]
                .sort((a, b) =>
                  (ACTION_META[a]?.label || a).localeCompare(ACTION_META[b]?.label || b, undefined, { sensitivity: "base" })
                )
                .map((a) => <SelectItem key={a} value={a}>{ACTION_META[a]?.label || a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Input
          data-testid="audit-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search actor or target…"
          className="h-9 rounded-md max-w-sm"
        />
      </div>

      <div className="px-8 py-2 trk-fade-in">
        {loading ? (
          <div className="bg-white border border-border rounded-lg shadow-sm p-12 text-center">
            <div className="trk-uppercase-label">Loading…</div>
          </div>
        ) : (
          <DataTable
            title="Audit Log"
            storageKey="auditLog.visibleColumns"
            testIdPrefix="audit"
            columns={AUDIT_COLUMNS}
            rows={filtered}
            rowKey={(r) => r.id}
            pageSize={20}
            emptyTestId="audit-empty"
            emptyMessage="No matching events yet. Actions like invites, deactivations, password/2FA changes will show up here."
          />
        )}
      </div>
    </AppLayout>
  );
}
